import jsQR from 'jsqr';
import {
  buildVectorEpsDocument,
  encodeRgbaToPngBytes,
  isFinderPatternModule,
  rasterizeQrToRgba,
} from './qr-export-formats.js';
import {
  extractModuleCellsFromSvgPath,
  generateGs1QrPrintAssets,
  measureQrSvgGeometry,
  type GenerateGs1QrPrintAssetsInput,
  type GeneratedGs1QrPrintAssets,
  type Gs1SymbolSpecTableId,
} from './qr-generator.js';
import {
  STYLED_QR_DISCLAIMER_LABEL,
  evaluateQrStyleRules,
  formatCssHex,
  normalizeHexCode,
  type QrErrorCorrectionLevel,
  type QrStyleConfig,
} from './qr-style-rules.js';

export { extractModuleCellsFromSvgPath };

function runJsQrDecode(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): { readonly data: string } | null {
  const fn = (
    jsQR as unknown as {
      readonly default?: (
        data: Uint8ClampedArray,
        w: number,
        h: number,
      ) => { readonly data: string } | null;
    } & ((data: Uint8ClampedArray, w: number, h: number) => { readonly data: string } | null)
  ).default
    ? (
        jsQR as unknown as {
          readonly default: (
            data: Uint8ClampedArray,
            w: number,
            h: number,
          ) => { readonly data: string } | null;
        }
      ).default
    : (jsQR as unknown as (
        data: Uint8ClampedArray,
        w: number,
        h: number,
      ) => { readonly data: string } | null);
  return fn(rgba, width, height);
}

/**
 * Rasterizes the styled QR symbol (including foreground/background colours, dot shape, quiet zone,
 * and centre logo overlay) into an RGBA pixel buffer and runs a `jsQR` decode test.
 */
export function verifyStyledQrDecode(params: {
  readonly expectedUrl: string;
  readonly moduleCount: number;
  readonly cells: readonly { readonly col: number; readonly row: number }[];
  readonly style: QrStyleConfig;
}): {
  readonly decodePassed: boolean;
  readonly decodedUrl: string | null;
} {
  const { expectedUrl, moduleCount, cells, style } = params;
  const effectiveQuietZone = style.quietZoneModules ?? 4;
  if (moduleCount <= 0 || cells.length === 0 || effectiveQuietZone < 4) {
    return { decodePassed: false, decodedUrl: null };
  }
  const { rgba, size } = rasterizeQrToRgba({
    moduleCount,
    cells,
    pixelsPerModule: 10,
    style,
  });
  const decoded = runJsQrDecode(rgba, size, size);
  const decodedUrl = decoded?.data ?? null;
  return { decodePassed: decodedUrl === expectedUrl, decodedUrl };
}

export interface GeneratedStyledQrAssets extends GeneratedGs1QrPrintAssets {
  readonly disclaimerLabel: typeof STYLED_QR_DISCLAIMER_LABEL;
  readonly decodePassed: boolean;
  readonly decodedUrl: string | null;
  readonly ruleStatus: 'valid' | string;
}

/**
 * Generates styled SVG, PDF, PNG, and EPS QR assets using error correction level `'H'`, custom colours,
 * dot shape, and optional centre logo, and runs the decode test against the exact GS1 Digital Link URL.
 */
export function generateStyledQrAssets(
  input: GenerateGs1QrPrintAssetsInput & { readonly style: QrStyleConfig },
): GeneratedStyledQrAssets {
  const { style, ...baseInput } = input;
  const tableId: Gs1SymbolSpecTableId = baseInput.tableId ?? 'TABLE_5_46';
  const effectiveEcLevel: QrErrorCorrectionLevel =
    style.errorCorrectionLevel ?? baseInput.errorCorrectionLevel ?? 'H';
  const baseAssets = generateGs1QrPrintAssets({
    ...baseInput,
    tableId,
    errorCorrectionLevel: effectiveEcLevel,
  });
  const geometry = measureQrSvgGeometry(baseAssets.svg);
  const viewBoxSize = geometry.viewBoxSize;
  const moduleSize = geometry.moduleSize;
  const moduleCount = geometry.moduleCountWithQuietZone;
  const pathMatch = /<path[^>]*\bd="([^"]+)"/.exec(baseAssets.svg);
  const rawPathD = pathMatch?.[1] ?? '';
  const fgColor = formatCssHex(style.fgHex);
  const bgColor = formatCssHex(style.bgHex);
  const cells = extractModuleCellsFromSvgPath(rawPathD, moduleSize, moduleCount);
  const shapesSvg: string[] = [];
  if (style.dotShape === 'square' || cells.length === 0) {
    shapesSvg.push(`<path d="${rawPathD}" fill="${fgColor}" fill-rule="evenodd" />`);
  } else {
    const effectiveQz = style.quietZoneModules ?? 4;
    const rx = (moduleSize * 0.32).toFixed(2);
    const r = (moduleSize * 0.44).toFixed(2);
    const s = moduleSize.toFixed(2);
    for (const cell of cells) {
      const x = (cell.col * moduleSize).toFixed(2);
      const y = (cell.row * moduleSize).toFixed(2);
      if (isFinderPatternModule(cell.col, cell.row, moduleCount, effectiveQz)) {
        shapesSvg.push(`<rect x="${x}" y="${y}" width="${s}" height="${s}" fill="${fgColor}" />`);
      } else if (style.dotShape === 'rounded') {
        shapesSvg.push(
          `<rect x="${x}" y="${y}" width="${s}" height="${s}" rx="${rx}" fill="${fgColor}" />`,
        );
      } else {
        const cx = ((cell.col + 0.5) * moduleSize).toFixed(2);
        const cy = ((cell.row + 0.5) * moduleSize).toFixed(2);
        shapesSvg.push(`<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fgColor}" />`);
      }
    }
  }
  let logoOverlaySvg = '';
  if (style.showLogo && style.logoScalePercent > 0) {
    const logoBoxSize = Number(((viewBoxSize * style.logoScalePercent) / 100).toFixed(2));
    const logoOffset = Number(((viewBoxSize - logoBoxSize) / 2).toFixed(2));
    const rx = Number((logoBoxSize * 0.2).toFixed(2));
    const center = Number((viewBoxSize / 2).toFixed(2));
    const fontSize = Math.max(6, Math.round(logoBoxSize * 0.42));
    const safeLabel = (style.logoLabel.trim() || 'MP').slice(0, 4).replace(/[<>&"']/g, '') || 'MP';
    logoOverlaySvg = `<g data-qr-logo="true"><rect x="${String(logoOffset)}" y="${String(logoOffset)}" width="${String(logoBoxSize)}" height="${String(logoBoxSize)}" rx="${String(rx)}" fill="${bgColor}" stroke="${fgColor}" stroke-width="1.5" /><text x="${String(center)}" y="${String(center)}" text-anchor="middle" dominant-baseline="central" fill="${fgColor}" font-size="${String(fontSize)}" font-weight="700">${safeLabel}</text></g>`;
  }
  const effectiveStyle: QrStyleConfig = { ...style, errorCorrectionLevel: effectiveEcLevel };
  const { decodePassed, decodedUrl } = verifyStyledQrDecode({
    expectedUrl: baseAssets.url,
    moduleCount,
    cells,
    style: effectiveStyle,
  });
  const ruleEval = evaluateQrStyleRules(effectiveStyle, { decodePassed });
  const styledSvg = `<svg viewBox="0 0 ${String(viewBoxSize)} ${String(viewBoxSize)}" width="${baseAssets.physicalWidthMm.toFixed(3)}mm" height="${baseAssets.physicalHeightMm.toFixed(3)}mm" xmlns="http://www.w3.org/2000/svg" data-qr-variant="styled" data-qr-ec-level="${effectiveEcLevel}" data-gs1-x-dim-mm="${baseAssets.xDimensionMm.toFixed(3)}" data-gs1-quiet-zone="4X" data-dot-shape="${style.dotShape}" data-fg-hex="${normalizeHexCode(style.fgHex)}" data-bg-hex="${normalizeHexCode(style.bgHex)}" data-logo-enabled="${String(style.showLogo)}" data-qr-disclaimer="${STYLED_QR_DISCLAIMER_LABEL}" data-gs1-dl-url="${baseAssets.url}"><rect width="${String(viewBoxSize)}" height="${String(viewBoxSize)}" fill="${bgColor}" />${shapesSvg.join('')}${logoOverlaySvg}</svg>`;

  const styledEps = buildVectorEpsDocument({
    url: baseAssets.url,
    clauseRef: baseAssets.spec.clauseRef,
    xDimensionMm: baseAssets.xDimensionMm,
    physicalSizeMm: baseAssets.physicalWidthMm,
    viewBoxSize,
    svgPathD: rawPathD,
    moduleCount,
    cells,
    style: effectiveStyle,
  });

  const { rgba, size } = rasterizeQrToRgba({
    moduleCount,
    cells,
    pixelsPerModule: 8,
    style: effectiveStyle,
  });
  const styledPng = encodeRgbaToPngBytes(rgba, size, size);

  return {
    ...baseAssets,
    svg: styledSvg,
    eps: styledEps,
    png: styledPng,
    disclaimerLabel: STYLED_QR_DISCLAIMER_LABEL,
    decodePassed,
    decodedUrl,
    ruleStatus: ruleEval.status,
  };
}
